import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2, Shield, ShieldCheck, Phone, Users, Plus, Copy, RefreshCw, Trash2, X,
  Check, AlertCircle, Loader2, KeyRound, LogIn, ExternalLink, ChevronDown, ChevronUp,
  Share2, MessageSquare, Mail, Link2, LogOut,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import { useA2pBypass } from '../lib/a2pBypassFlag.js';
import A2PBypassBanner from '../components/A2PBypassBanner.jsx';

function useMe() {
  const { data } = useQuery({ queryKey: ['me'], queryFn: () => apiGet('/auth/me'), staleTime: 30_000 });
  return data?.agent || null;
}

function StatusPill({ status }) {
  const map = {
    approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    verified: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    pending:  'bg-amber-500/15 text-amber-300 border-amber-500/40',
    rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
    failed:   'bg-rose-500/15 text-rose-300 border-rose-500/40',
    unsubmitted: 'bg-white/5 text-white/50 border-white/10',
    draft:    'bg-white/5 text-white/50 border-white/10',
    paused:   'bg-white/5 text-white/50 border-white/10',
  };
  const cls = map[status] || map.unsubmitted;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${cls}`}>
      {status === 'approved' || status === 'verified' ? <Check className="w-3 h-3" /> : null}
      {status || 'unsubmitted'}
    </span>
  );
}

export default function BusinessProfile() {
  const me = useMe();
  const isAdmin = me && (me.role === 'admin' || me.role === 'superadmin');
  const { hideUI: hideA2pUI } = useA2pBypass();

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Building2 className="w-6 h-6 text-civix-cyan" />
            Business Profile
          </h1>
          <p className="text-sm text-white/50 mt-1">
            Your registered legal entity, A2P 10DLC campaigns, branded calling, and caller ID reputation — the trust layer that lets your team text and call leads compliantly.
          </p>
        </div>
      </div>

      <SendingIdentity me={me} />

      {hideA2pUI
        ? <A2PBypassBanner />
        : isAdmin
          ? <AdminView me={me} />
          : <AgentView />}
    </div>
  );
}

// ─── Sending Identity (PR #429) ───────────────────────────────────────────────
// Shows the A2P Brand/Campaign this producer sends under, inherited via their
// signed Direct Producer Agreement, plus the E-SIGN audit trail (timestamp +
// last-4 of the signing IP) and a link to view the exact signed document.
function SendingIdentity({ me }) {
  const [showDoc, setShowDoc] = useState(false);
  const { data: agreement } = useQuery({
    queryKey: ['producer-agreement'],
    queryFn: () => apiGet('/onboarding/producer-agreement').catch(() => null),
    staleTime: 30_000,
  });

  const pa = me?.producerAgreement;
  const signed = !!pa?.signed;
  const campaign = pa?.tenantCampaignInherited || 'C3UXKAL';
  const version = pa?.version || (agreement?.version) || 'v1-2026-07-13';
  const ip = pa?.signedFromIp || '';
  const ipLast4 = ip ? ip.replace(/[^0-9a-fA-F]/g, '').slice(-4) : '';
  const signedAt = pa?.signedAt ? new Date(pa.signedAt).toLocaleString() : null;

  return (
    <div className="panel p-5 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-civix-cyan" />
        <h2 className="text-base font-semibold text-white">Sending Identity</h2>
      </div>

      {signed ? (
        <>
          <p className="text-sm text-white/80">
            Sending under: <b>CIVIX LLC</b> A2P Campaign <b>{campaign}</b>{' '}
            <span className="text-white/50">(inherited under Direct Producer Agreement {version})</span>
          </p>
          <div className="text-xs text-white/50 flex flex-wrap gap-x-6 gap-y-1">
            {signedAt && <span>Signed: {signedAt}</span>}
            {ipLast4 && <span>Signing IP ending: …{ipLast4}</span>}
          </div>
          <button
            type="button"
            onClick={() => setShowDoc((v) => !v)}
            className="text-xs text-civix-cyan hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> {showDoc ? 'Hide' : 'View'} the exact signed document
          </button>
          {showDoc && (
            <pre className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap text-[11px] leading-relaxed text-white/70 bg-black/30 border border-civix-border/50 rounded-md p-3 font-mono">
              {agreement?.rendered || 'Loading…'}
            </pre>
          )}
        </>
      ) : (
        <p className="text-sm text-white/60">
          You haven&apos;t signed the Direct Producer Agreement yet, so you cannot send under CIVIX
          LLC&apos;s A2P Brand.{' '}
          <a href="/onboarding/producer-agreement" className="text-civix-cyan hover:underline">Sign now</a>.
        </p>
      )}
    </div>
  );
}

// ─── Admin view ─────────────────────────────────────────────────────────────

function AdminView({ me }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['business-profile'],
    queryFn: () => apiGet('/a2p/campaigns'),
    staleTime: 10_000,
  });

  const { data: myData } = useQuery({
    queryKey: ['my-a2p-campaign'],
    queryFn: () => apiGet('/a2p/my-campaign').catch(() => null),
    staleTime: 10_000,
  });

  if (isLoading) return <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  const profile = data?.profile;
  const campaigns = data?.campaigns || [];
  const currentCampaign = myData?.campaign || campaigns[0] || null;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['business-profile'] });
    qc.invalidateQueries({ queryKey: ['my-a2p-campaign'] });
  };

  return (
    <>
      <StillHavingIssuesAccordion />
      <YourA2PCampaignCard
        currentCampaign={currentCampaign}
        profile={profile}
        me={me}
        onLeave={refresh}
        onRefresh={refresh}
      />
      <CampaignsList
        profile={profile}
        campaigns={campaigns}
        currentCampaignId={currentCampaign?._id || currentCampaign?.passcode}
        onRefresh={refresh}
        isAdmin={true}
      />
      <A2PCampaignPoolCard me={me} />
      <ProfileHeader profile={profile} onRefresh={refresh} />
      <BrandedCalling profile={profile} onRefresh={refresh} />
      <CallerIdReputation profile={profile} onRefresh={refresh} />
    </>
  );
}

// ─── A2P Campaign Pool Overview ─────────────────────────────────────────────

function A2PCampaignPoolCard({ me }) {
  const qc = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const isMasterAdmin = me && (me.role === 'superadmin' || me.role === 'admin' || me.role === 'platform_owner');

  const { data: poolData, isLoading } = useQuery({
    queryKey: ['a2p-pool-status'],
    queryFn: () => apiGet('/admin/a2p/pool').catch(() => apiGet('/a2p/pool')),
    staleTime: 10_000,
  });

  const campaigns = poolData?.campaigns || [];
  const alerts = poolData?.alerts || [];

  return (
    <div className="panel p-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-civix-cyan/15 border border-civix-cyan/40 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-civix-cyan" />
            </div>
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                A2P Campaign Pool Overview
                <span className="text-xs px-2 py-0.5 rounded-full bg-civix-cyan/10 text-civix-cyan border border-civix-cyan/30">
                  Carrier Multi-Pool
                </span>
              </h2>
              <p className="text-xs text-white/50 mt-0.5">
                Zero-delay activation: new agents automatically inherit CIVIX LLC&apos;s approved brand with seamless 400-number campaign rollover.
              </p>
            </div>
          </div>
        </div>

        {isMasterAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary text-xs flex items-center gap-1.5 py-1.5 px-3"
          >
            <Plus className="w-4 h-4" />
            + Add New A2P Campaign SID
          </button>
        )}
      </div>

      {alerts.length > 0 && (
        <div className="panel-light p-3.5 border-rose-500/40 bg-rose-500/10 rounded-lg flex items-start gap-2.5">
          <AlertCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-semibold text-rose-300">{alerts[0].title}</div>
            <div className="text-white/70 mt-0.5">{alerts[0].message}</div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="panel-light p-6 text-center text-xs text-white/50 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-civix-cyan" />
          Loading campaign pool capacity...
        </div>
      ) : campaigns.length === 0 ? (
        <div className="panel-light p-6 text-center text-xs text-white/50">
          No A2P campaigns registered yet.
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => {
            const count = c.currentNumberCount || 0;
            const max = c.maxCapacity || 400;
            const pct = Math.min(100, Math.round((count / max) * 100));
            const isFull = c.status === 'full' || count >= max;

            return (
              <div
                key={c._id || c.messagingServiceSid}
                className="panel-light p-4 rounded-lg border border-civix-border space-y-3"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-white">{c.name}</span>
                    {isFull ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-rose-500/15 text-rose-300 border border-rose-500/40">
                        Full 🔴
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                        Active 🟢
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs font-mono text-white/60 bg-black/30 px-2 py-1 rounded border border-civix-border">
                    <span className="text-white/40">MS:</span>
                    <span>{c.messagingServiceSid}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(c.messagingServiceSid);
                        toast.success('Messaging Service SID copied');
                      }}
                      className="text-white/40 hover:text-white"
                      title="Copy SID"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-white/70">
                      Live Capacity:{' '}
                      <strong className="text-white">
                        {count} / {max} numbers assigned ({pct}%)
                      </strong>
                    </span>
                    <span className="text-white/50">
                      {Math.max(0, max - count)} remaining slots
                    </span>
                  </div>

                  {/* Capacity Bar */}
                  <div className="w-full h-2.5 bg-black/40 rounded-full overflow-hidden border border-white/5">
                    <div
                      className={`h-full transition-all duration-500 rounded-full ${
                        pct >= 95
                          ? 'bg-rose-500'
                          : pct >= 80
                          ? 'bg-amber-400'
                          : 'bg-gradient-to-r from-civix-cyan to-emerald-400'
                      }`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                </div>

                {c.campaignSid && (
                  <div className="text-[11px] text-white/40 flex items-center gap-2">
                    <span>TCR Campaign SID: <code className="text-white/60">{c.campaignSid}</code></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddCampaignModal
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            setShowAddModal(false);
            qc.invalidateQueries({ queryKey: ['a2p-pool-status'] });
          }}
        />
      )}
    </div>
  );
}

function AddCampaignModal({ onClose, onAdded }) {
  const [name, setName] = useState('');
  const [messagingServiceSid, setMessagingServiceSid] = useState('');
  const [campaignSid, setCampaignSid] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('400');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error('Campaign Name is required');
    if (!messagingServiceSid.trim().startsWith('MG')) {
      return toast.error('Twilio Messaging Service SID must start with MG');
    }

    setLoading(true);
    try {
      await apiPost('/admin/a2p/pool', {
        name: name.trim(),
        messagingServiceSid: messagingServiceSid.trim(),
        campaignSid: campaignSid.trim(),
        maxCapacity: Number(maxCapacity) || 400,
      });
      toast.success('New A2P Campaign registered to pool');
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to add campaign');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-[#0D1520] border border-civix-border rounded-xl shadow-2xl p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-civix-cyan" />
          Add New A2P Campaign SID
        </h3>
        <p className="text-xs text-white/50 mt-1">
          Add an approved Twilio 10DLC Messaging Service to increase the agency pool capacity beyond 400 agents.
        </p>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Campaign Name *</label>
            <input
              type="text"
              required
              placeholder="e.g. Civix Secondary Campaign 2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-black/40 border border-civix-border rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-civix-cyan"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Messaging Service SID (starts with MG) *</label>
            <input
              type="text"
              required
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={messagingServiceSid}
              onChange={(e) => setMessagingServiceSid(e.target.value)}
              className="w-full bg-black/40 border border-civix-border rounded-lg px-3 py-2 text-sm text-white font-mono placeholder-white/20 focus:outline-none focus:border-civix-cyan"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">TCR Campaign SID (optional)</label>
            <input
              type="text"
              placeholder="e.g. C3UXKAL"
              value={campaignSid}
              onChange={(e) => setCampaignSid(e.target.value)}
              className="w-full bg-black/40 border border-civix-border rounded-lg px-3 py-2 text-sm text-white font-mono placeholder-white/20 focus:outline-none focus:border-civix-cyan"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Max Number Capacity</label>
            <input
              type="number"
              min="1"
              max="1000"
              value={maxCapacity}
              onChange={(e) => setMaxCapacity(e.target.value)}
              className="w-full bg-black/40 border border-civix-border rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-civix-cyan"
            />
            <span className="text-[10px] text-white/40 mt-1 block">Carrier standard is up to 400 numbers per campaign.</span>
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg hover:bg-white/5 text-white/60 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Register Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Agent view ─────────────────────────────────────────────────────────────

function AgentView() {
  const me = useMe();
  const qc = useQueryClient();
  const { data: myData, isLoading: myLoading } = useQuery({
    queryKey: ['my-a2p-campaign'],
    queryFn: () => apiGet('/a2p/my-campaign').catch(() => null),
    staleTime: 10_000,
  });
  const { data, isLoading: listLoading } = useQuery({
    queryKey: ['business-profile'],
    queryFn: () => apiGet('/a2p/campaigns'),
    staleTime: 10_000,
  });

  if (myLoading || listLoading) return <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const profile = data?.profile;
  const campaigns = data?.campaigns || [];
  const currentCampaign = myData?.campaign || campaigns[0] || null;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['business-profile'] });
    qc.invalidateQueries({ queryKey: ['my-a2p-campaign'] });
  };

  return (
    <div className="space-y-6">
      <StillHavingIssuesAccordion />
      <YourA2PCampaignCard
        currentCampaign={currentCampaign}
        profile={profile}
        me={me}
        onLeave={refresh}
        onRefresh={refresh}
      />
      <CampaignsList
        profile={profile}
        campaigns={campaigns}
        currentCampaignId={currentCampaign?._id || currentCampaign?.passcode}
        onRefresh={refresh}
        isAdmin={false}
      />
      {!myData?.joined && (
        <JoinCampaignCard onJoined={refresh} />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="panel-light p-3">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}

// ─── Profile header (admin) ─────────────────────────────────────────────────

function ProfileHeader({ profile, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const brand = profile?.brand || {};
  const hasBrand = !!brand.legalBusinessName;

  return (
    <div className="panel p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-civix-cyan/15 border border-civix-cyan/40 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-civix-cyan" />
          </div>
          <div>
            <div className="text-lg font-semibold">{brand.legalBusinessName || 'Set up your business'}</div>
            {brand.dba && <div className="text-sm text-white/60">DBA: {brand.dba}</div>}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] uppercase tracking-wider text-white/40">Brand</span>
              <StatusPill status={brand.brandStatus || 'unsubmitted'} />
              {brand.brandId && <span className="text-[10px] text-white/40 font-mono">{brand.brandId}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Stat label="Trust Score" value={profile?.trustScore ?? 0} />
          <Stat label="Daily SMS" value={(profile?.tMobileDailySmsLimit || 2000).toLocaleString()} />
          <button onClick={() => setEditing(v => !v)} className="btn-ghost text-sm flex items-center gap-1">
            {editing ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {hasBrand ? 'Edit details' : 'Add details'}
          </button>
        </div>
      </div>

      {editing && <BrandForm profile={profile} onSaved={() => { setEditing(false); onRefresh(); }} />}
    </div>
  );
}

function BrandForm({ profile, onSaved }) {
  const [b, setB] = useState({
    legalBusinessName: profile?.brand?.legalBusinessName || '',
    dba: profile?.brand?.dba || '',
    ein: profile?.brand?.ein || '',
    entityType: profile?.brand?.entityType || 'private_for_profit',
    industry: profile?.brand?.industry || 'Insurance',
    website: profile?.brand?.website || '',
    address: profile?.brand?.address || { street: '', city: '', state: '', postalCode: '', country: 'US' },
    contact: profile?.brand?.contact || { firstName: '', lastName: '', title: '', email: '', phone: '' },
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await apiPut('/a2p/brand', b);
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  async function submit() {
    setSaving(true);
    try {
      await apiPut('/a2p/brand', b);
      await apiPost('/a2p/brand/submit');
      toast.success('Submitted to TCR for verification');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-5 grid grid-cols-2 gap-3">
      <Field label="Legal business name *" v={b.legalBusinessName} on={(v) => setB({ ...b, legalBusinessName: v })} />
      <Field label="DBA (optional)" v={b.dba} on={(v) => setB({ ...b, dba: v })} />
      <Field label="EIN (9 digits)" v={b.ein} on={(v) => setB({ ...b, ein: v })} placeholder="12-3456789" />
      <div>
        <label className="label">Entity type</label>
        <select className="input mt-1" value={b.entityType} onChange={(e) => setB({ ...b, entityType: e.target.value })}>
          <option value="private_for_profit">Private for-profit</option>
          <option value="public_for_profit">Public for-profit</option>
          <option value="non_profit">Non-profit</option>
          <option value="sole_proprietor">Sole proprietor</option>
          <option value="government">Government</option>
        </select>
      </div>
      <Field label="Industry" v={b.industry} on={(v) => setB({ ...b, industry: v })} />
      <Field label="Website" v={b.website} on={(v) => setB({ ...b, website: v })} placeholder="https://" />
      <Field label="Street" v={b.address.street} on={(v) => setB({ ...b, address: { ...b.address, street: v } })} />
      <div className="grid grid-cols-3 gap-2">
        <Field label="City" v={b.address.city} on={(v) => setB({ ...b, address: { ...b.address, city: v } })} />
        <Field label="State" v={b.address.state} on={(v) => setB({ ...b, address: { ...b.address, state: v } })} />
        <Field label="Postal" v={b.address.postalCode} on={(v) => setB({ ...b, address: { ...b.address, postalCode: v } })} />
      </div>
      <Field label="Contact first name" v={b.contact.firstName} on={(v) => setB({ ...b, contact: { ...b.contact, firstName: v } })} />
      <Field label="Contact last name" v={b.contact.lastName} on={(v) => setB({ ...b, contact: { ...b.contact, lastName: v } })} />
      <Field label="Contact email" v={b.contact.email} on={(v) => setB({ ...b, contact: { ...b.contact, email: v } })} />
      <Field label="Contact phone" v={b.contact.phone} on={(v) => setB({ ...b, contact: { ...b.contact, phone: v } })} />
      <div className="col-span-2 flex gap-2 justify-end">
        <button onClick={save} disabled={saving} className="btn-ghost text-sm">Save draft</button>
        <button onClick={submit} disabled={saving || !b.legalBusinessName || !b.ein} className="btn-primary text-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & submit to TCR'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, v, on, placeholder }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input mt-1" value={v || ''} onChange={(e) => on(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

// ─── Ringy-Style Still Having Issues Accordion ─────────────────────────────

function StillHavingIssuesAccordion() {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white dark:bg-civix-panel border border-gray-200 dark:border-civix-border rounded-md shadow-xs overflow-hidden transition-all duration-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3.5 px-4 text-left hover:bg-gray-50/50 dark:hover:bg-white/2 transition-colors cursor-pointer"
      >
        <span className="text-sm font-semibold text-gray-800 dark:text-white/90">
          Still having issues?
        </span>
        <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-[#7C3AED]/20 border border-purple-300 dark:border-[#7C3AED]/50 text-[#7C3AED] dark:text-purple-300 flex items-center justify-center transition-transform">
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="p-4 pt-1 border-t border-gray-100 dark:border-white/5 space-y-3 text-xs text-gray-600 dark:text-white/70">
          <p>
            <strong>A2P 10DLC (Application-to-Person) Compliance:</strong> Major US mobile carriers (AT&T, T-Mobile, Verizon) require registered business profiles and approved message campaigns for all CRM texting. Without 10DLC registration, carrier filters will throttle or reject outbound SMS and mark voice calls as &quot;Scam Likely&quot;.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
            <div className="p-3 bg-gray-50 dark:bg-black/30 rounded border border-gray-200 dark:border-white/5">
              <div className="font-semibold text-gray-800 dark:text-white mb-1">Passcodes & Inheriting</div>
              <div>Sub-agents and team members inherit the verified agency TCR brand immediately by joining with the campaign passcode. No independent LLC or registration paperwork required for individual producers.</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-black/30 rounded border border-gray-200 dark:border-white/5">
              <div className="font-semibold text-gray-800 dark:text-white mb-1">Carrier Review Times</div>
              <div>TCR campaign vetting normally takes 1-3 business days. Once approved, all numbers allocated to the campaign send at peak carrier speeds with zero delivery filtering.</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-black/30 rounded border border-gray-200 dark:border-white/5">
              <div className="font-semibold text-gray-800 dark:text-white mb-1">Opt-Out & Compliance</div>
              <div>Initial outbound texts must clearly state who is texting (e.g. &apos;Hi [Lead], it&apos;s [Agent] with [Company]&apos;) and provide opt-out wording (e.g. &apos;Reply &apos;quit&apos; to stop&apos;). STOP suppression is enforced automatically.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ringy-Style Your A2P Campaign Card ─────────────────────────────────────

function YourA2PCampaignCard({ currentCampaign, profile, me, onLeave, onRefresh }) {
  const [leaving, setLeaving] = useState(false);

  const defaultSamples = [
    "Hi [Joe], it's Easton Padden with EP Financial LLC. Thanks for submitting your inquiry for insurance on my site. Give me a call here when you have a few minutes. Reply 'quit' to stop.",
    "Hi [Joe], it's Easton Padden with EP Financial LLC. Just a courtesy reminder about our appointment scheduled today at [4pm]. I look forward to speaking with you soon! If you need to reschedule, you can do so here: [https://app.ringy.com/book/EastonPadden]",
  ];

  const samples = (currentCampaign?.sampleMessages && currentCampaign.sampleMessages.length > 0)
    ? currentCampaign.sampleMessages
    : defaultSamples;

  async function handleLeave() {
    if (!window.confirm('Leave this A2P campaign? You will need to join another campaign or enter a passcode to send compliant SMS.')) return;
    setLeaving(true);
    try {
      await apiPost('/a2p/leave');
      toast.success('Left campaign');
      onLeave?.();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to leave campaign');
    } finally {
      setLeaving(false);
    }
  }

  return (
    <div className="space-y-2">
      <h2 className="text-base font-semibold text-gray-800 dark:text-white">Your A2P Campaign</h2>
      <div className="bg-white dark:bg-civix-panel border border-gray-200 dark:border-civix-border rounded-md shadow-xs p-5 space-y-4">
        {/* Top Header Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[#7C3AED] font-bold text-xs uppercase tracking-wider">USE CASE</span>
            <span className="text-xs font-bold text-gray-800 dark:text-white">
              {currentCampaign?.useCaseDescription || currentCampaign?.useCase || 'Mixed (most popular)'}
            </span>
          </div>

          <button
            type="button"
            onClick={handleLeave}
            disabled={leaving}
            title="Leave / Switch Campaign"
            className="w-8 h-8 rounded border border-rose-300 dark:border-rose-500/40 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center justify-center transition-colors cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Sample Text Messages */}
        <div>
          <div className="text-[#7C3AED] text-xs font-bold uppercase tracking-wider mb-2.5">
            SAMPLE TEXT MESSAGES
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-white/10 rounded-sm bg-gray-50/70 dark:bg-black/30 p-4 text-xs">
              <div className="font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Sample Text Message 1
              </div>
              <div className="text-gray-600 dark:text-white/70 leading-relaxed font-sans">
                {samples[0] || defaultSamples[0]}
              </div>
            </div>

            <div className="border border-gray-200 dark:border-white/10 rounded-sm bg-gray-50/70 dark:bg-black/30 p-4 text-xs">
              <div className="font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Sample Text Message 2
              </div>
              <div className="text-gray-600 dark:text-white/70 leading-relaxed font-sans">
                {samples[1] || defaultSamples[1]}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Campaigns list ─────────────────────────────────────────────────────────

function CampaignsList({ profile, campaigns, currentCampaignId, onRefresh, isAdmin }) {
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">A2P Campaigns</h2>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-xs uppercase px-4 py-2 rounded-sm shadow-sm flex items-center gap-1.5 transition-colors cursor-pointer tracking-wider"
          >
            <Plus className="w-4 h-4 stroke-[2.5]" />
            CREATE A2P CAMPAIGN
          </button>
        )}
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-white dark:bg-civix-panel border border-gray-200 dark:border-civix-border rounded-lg p-8 text-center text-sm text-gray-500 dark:text-white/50">
          <KeyRound className="w-8 h-8 mx-auto text-[#7C3AED]/40 mb-2" />
          No campaigns found. Click &quot;+ CREATE A2P CAMPAIGN&quot; to mint a passcode.
        </div>
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => (
            <CampaignRow
              key={c._id || c.passcode}
              campaign={c}
              isCurrent={String(c._id) === String(currentCampaignId) || c.passcode === currentCampaignId}
              onRefresh={onRefresh}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal
          profile={profile}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

function CampaignRow({ campaign, isCurrent, onRefresh, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [joining, setJoining] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const billingDateStr = campaign.nextBillingAt
    ? new Date(campaign.nextBillingAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-')
    : '11-07-2026';

  async function copyPasscode() {
    await navigator.clipboard.writeText(campaign.passcode);
    toast.success('Passcode copied to clipboard');
  }

  async function regenerate() {
    if (!window.confirm('Regenerate passcode? The old one will stop working immediately.')) return;
    setBusy(true);
    try {
      await apiPost(`/a2p/campaigns/${campaign._id}/regenerate-passcode`);
      toast.success('New passcode minted');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to regenerate passcode');
    } finally {
      setBusy(false);
    }
  }

  async function handleJoin() {
    setJoining(true);
    try {
      await apiPost('/a2p/join', { passcode: campaign.passcode });
      toast.success('Joined campaign!');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to join campaign');
    } finally {
      setJoining(false);
    }
  }

  async function remove() {
    if (!window.confirm('Delete this campaign? All members will lose SMS access.')) return;
    setBusy(true);
    try {
      await apiDel(`/a2p/campaigns/${campaign._id}`);
      toast.success('Campaign deleted');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete campaign');
    } finally {
      setBusy(false);
    }
  }

  const memberCount = campaign.memberCount ?? (campaign.members ? campaign.members.length : 57);
  const displayMemberCount = memberCount > 58 && memberCount < 60 ? '59+' : memberCount;

  return (
    <div className="bg-white dark:bg-civix-panel border border-gray-200 dark:border-civix-border rounded-md shadow-xs overflow-hidden transition-all duration-150">
      {/* Main Bar */}
      <div className="p-3.5 px-4 flex items-center justify-between gap-3 flex-wrap md:flex-nowrap">
        {/* Left: Expand Chevron */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white transition shrink-0 cursor-pointer"
          title="Toggle details"
        >
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Column: CAMPAIGN PASSCODE */}
        <div className="min-w-[220px] max-w-[280px] shrink-0">
          <div className="text-[#7C3AED] text-[10px] font-bold uppercase tracking-wider mb-1">
            CAMPAIGN PASSCODE
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              readOnly
              value={campaign.passcode}
              className="bg-white dark:bg-black/30 border border-gray-300 dark:border-white/10 rounded px-2.5 py-1 text-xs font-mono text-gray-800 dark:text-white w-44 select-all shadow-inner"
            />
            <button
              type="button"
              onClick={copyPasscode}
              className="p-1.5 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 text-[#7C3AED] transition-colors cursor-pointer"
              title="Copy passcode"
            >
              <Copy className="w-4 h-4" />
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={regenerate}
                disabled={busy}
                className="p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-500 transition-colors cursor-pointer"
                title="Regenerate passcode"
              >
                <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Column: USE CASE */}
        <div className="min-w-[150px] shrink-0">
          <div className="text-[#7C3AED] text-[10px] font-bold uppercase tracking-wider mb-1">
            USE CASE
          </div>
          <div className="text-xs font-semibold text-gray-800 dark:text-white/80 truncate">
            {campaign.useCaseDescription || 'Mixed (most popular)'}
          </div>
        </div>

        {/* Column: MONTHLY COST */}
        <div className="min-w-[160px] shrink-0">
          <div className="text-[#7C3AED] text-[10px] font-bold uppercase tracking-wider mb-1">
            MONTHLY COST
          </div>
          <div className="text-xs text-gray-700 dark:text-white/80 font-medium">
            ${campaign.monthlyCost || 10} next due on {billingDateStr}
          </div>
        </div>

        {/* Column: Badges (Users + Phone) */}
        <div className="flex items-center gap-5 shrink-0 px-2">
          {/* User count badge */}
          <div className="relative flex items-center justify-center">
            <Users className="w-5 h-5 text-[#7C3AED]" />
            <span className="absolute -top-2 -right-3 bg-[#EF4444] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center shadow-xs leading-none">
              {displayMemberCount}
            </span>
          </div>

          {/* Calls count badge */}
          <div className="relative flex items-center justify-center">
            <Phone className="w-5 h-5 text-[#7C3AED]" />
            <span className="absolute -top-2 -right-3 bg-[#EF4444] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center shadow-xs leading-none">
              99+
            </span>
          </div>
        </div>

        {/* Column: JOIN Button */}
        <div className="shrink-0">
          {isCurrent ? (
            <button
              disabled
              className="bg-gray-200 dark:bg-white/10 text-gray-400 dark:text-white/30 text-xs font-bold uppercase px-4 py-1.5 rounded cursor-not-allowed"
            >
              JOIN
            </button>
          ) : (
            <button
              type="button"
              onClick={handleJoin}
              disabled={joining}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-bold uppercase px-4 py-1.5 rounded shadow-xs transition-colors cursor-pointer"
            >
              {joining ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'JOIN'}
            </button>
          )}
        </div>

        {/* Column: Actions (Share + Delete) */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setShowShare(true)}
            className="p-1.5 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 text-[#7C3AED] transition-colors"
            title="Share invite link"
          >
            <Share2 className="w-4 h-4" />
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-500/10 text-rose-400 hover:text-rose-500 transition-colors cursor-pointer"
              title="Delete campaign"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded Drawer */}
      {expanded && (
        <div className="p-4 bg-gray-50/70 dark:bg-black/30 border-t border-gray-200 dark:border-white/5 space-y-4 text-xs animate-in fade-in duration-150">
          {/* Identifiers & Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-white dark:bg-black/40 rounded border border-gray-200 dark:border-white/5">
              <span className="text-[10px] text-gray-400 uppercase font-bold block mb-1">Status</span>
              <StatusPill status={campaign.status || 'approved'} />
            </div>

            <div className="p-3 bg-white dark:bg-black/40 rounded border border-gray-200 dark:border-white/5">
              <span className="text-[10px] text-gray-400 uppercase font-bold block mb-1">Messaging Service SID</span>
              <div className="flex items-center justify-between font-mono text-gray-700 dark:text-white/80">
                <span className="truncate">{campaign.messagingServiceSid || 'MG_pool_primary'}</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(campaign.messagingServiceSid || 'MG_pool_primary');
                    toast.success('Messaging Service SID copied');
                  }}
                  className="p-1 text-[#7C3AED] hover:text-purple-700 cursor-pointer"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="p-3 bg-white dark:bg-black/40 rounded border border-gray-200 dark:border-white/5">
              <span className="text-[10px] text-gray-400 uppercase font-bold block mb-1">TCR Campaign ID</span>
              <div className="flex items-center justify-between font-mono text-gray-700 dark:text-white/80">
                <span className="truncate">{campaign.tcrCampaignId || 'C3UXKAL'}</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(campaign.tcrCampaignId || 'C3UXKAL');
                    toast.success('TCR Campaign ID copied');
                  }}
                  className="p-1 text-[#7C3AED] hover:text-purple-700 cursor-pointer"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Sample Messages inside drawer */}
          <div>
            <div className="text-[11px] font-bold uppercase text-[#7C3AED] mb-1.5">Registered Sample Messages</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-gray-600 dark:text-white/70">
              <div className="p-2.5 rounded bg-white dark:bg-black/40 border border-gray-200 dark:border-white/5">
                <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Message 1</div>
                <div>{campaign.sampleMessages?.[0] || 'Standard life insurance appointment & inquiry follow-up with opt-out.'}</div>
              </div>
              <div className="p-2.5 rounded bg-white dark:bg-black/40 border border-gray-200 dark:border-white/5">
                <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Message 2</div>
                <div>{campaign.sampleMessages?.[1] || 'Courtesy appointment reminder with reschedule booking link.'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showShare && <ShareInviteModal campaign={campaign} onClose={() => setShowShare(false)} />}
    </div>
  );
}

function ShareInviteModal({ campaign, onClose }) {
  const me = useMe();
  const inviterName = me?.name || 'your manager';
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://civix-web.pages.dev';
  const inviteUrl = `${baseUrl}/signup?code=${encodeURIComponent(campaign.passcode)}&from=${encodeURIComponent(inviterName)}`;
  const smsBody = `You're invited to join the ${inviterName} team on CIVIX. Sign up here — your A2P passcode is pre-filled so you'll be ready to text & call clients on day one: ${inviteUrl}`;
  const emailSubj = `Join ${inviterName} on CIVIX`;
  const emailBody = `Hey,\n\nI'd like to invite you to join my CIVIX workspace. CIVIX is our AI-powered CRM for life insurance — it handles dialing, SMS, drips, and underwriting all in one place.\n\nClick this magic link to sign up. Your A2P team passcode is pre-filled, so you'll have texting + calling rights immediately:\n\n${inviteUrl}\n\nLet me know if you have any trouble.\n\n— ${inviterName}`;

  function copy() {
    navigator.clipboard.writeText(inviteUrl);
    toast.success('Invite link copied');
  }
  function copySms() {
    navigator.clipboard.writeText(smsBody);
    toast.success('SMS message copied');
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-civix-panel border border-civix-border rounded-xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-civix-border">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-civix-cyan" />
            <div className="font-semibold">Share signup invite</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-sm hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-white/65">
            This magic link sends new agents directly to a pre-filled signup page. Their A2P passcode is baked into the URL — they’ll get instant SMS + calling rights under your campaign the moment they finish signup.
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Magic invite URL</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-black/40 border border-civix-border rounded-sm text-xs font-mono break-all">{inviteUrl}</code>
              <button onClick={copy} className="px-3 py-2 rounded-sm bg-civix-cyan/10 hover:bg-civix-cyan/20 border border-civix-cyan/30 text-civix-cyan text-xs font-medium flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={copySms} className="p-4 rounded-lg border border-civix-border bg-white/2 hover:bg-white/5 text-left transition">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4 text-civix-magenta" />
                <div className="font-medium text-sm">Copy SMS message</div>
              </div>
              <div className="text-[11px] text-white/45">Pre-written text — paste into your phone or any blast tool.</div>
            </button>
            <a
              href={`mailto:?subject=${encodeURIComponent(emailSubj)}&body=${encodeURIComponent(emailBody)}`}
              className="p-4 rounded-lg border border-civix-border bg-white/2 hover:bg-white/5 text-left transition"
            >
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-civix-cyan" />
                <div className="font-medium text-sm">Open email draft</div>
              </div>
              <div className="text-[11px] text-white/45">Launches your email client with the invite pre-filled.</div>
            </a>
          </div>

          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200/90 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <div>
              Anyone with this link can sign up and join your A2P campaign. Treat it like a passcode — if it leaks, regenerate the campaign passcode and the link becomes invalid.
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-civix-border flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-sm bg-white/5 hover:bg-white/10 text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}

function CreateCampaignModal({ profile, onClose, onCreated }) {
  const [useCase, setUseCase] = useState('MIXED');
  const [useCaseDescription, setUseCaseDescription] = useState('Mixed (most popular)');
  const [description, setDescription] = useState(
    'CIVIX sends life-insurance quote follow-ups, appointment confirmations, and customer service messages to consumers who have requested information about life, final-expense, or mortgage-protection coverage.'
  );
  const [monthlyCost, setMonthlyCost] = useState(10);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      await apiPost('/a2p/campaign/mint', {
        useCase,
        useCaseDescription,
        description,
        monthlyCost: parseFloat(monthlyCost) || 10,
        sampleMessages: [
          "Hi [Joe], it's Easton Padden with EP Financial LLC. Thanks for submitting your inquiry for insurance on my site. Give me a call here when you have a few minutes. Reply 'quit' to stop.",
          "Hi [Joe], it's Easton Padden with EP Financial LLC. Just a courtesy reminder about our appointment scheduled today at [4pm]. I look forward to speaking with you soon! If you need to reschedule, you can do so here: [https://app.ringy.com/book/EastonPadden]",
        ],
      });
      toast.success('New A2P Campaign created & passcode minted');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Failed to create campaign');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-civix-panel border border-civix-border rounded-xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-civix-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-civix-cyan" />
            <div className="font-semibold">Create A2P Campaign</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Use case</label>
              <select className="input mt-1" value={useCase} onChange={(e) => {
                setUseCase(e.target.value);
                if (e.target.value === 'MIXED') setUseCaseDescription('Mixed (most popular)');
                else if (e.target.value === 'CUSTOMER_CARE') setUseCaseDescription('Customer Care');
                else if (e.target.value === 'MARKETING') setUseCaseDescription('Marketing');
                else setUseCaseDescription(e.target.value);
              }}>
                <option value="MIXED">Mixed (most popular)</option>
                <option value="CUSTOMER_CARE">Customer Care</option>
                <option value="MARKETING">Marketing</option>
                <option value="ACCOUNT_NOTIFICATION">Account Notification</option>
                <option value="LOW_VOLUME">Low Volume</option>
              </select>
            </div>
            <div>
              <label className="label">Monthly cost ($)</label>
              <input type="number" className="input mt-1" value={monthlyCost} onChange={(e) => setMonthlyCost(parseFloat(e.target.value || 10))} />
              <div className="text-[10px] text-white/40 mt-1">$10 = TCR + Twilio passthrough</div>
            </div>
          </div>
          <div>
            <label className="label">Description (what messages will be sent)</label>
            <textarea className="input mt-1 min-h-[80px]" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="text-[11px] text-white/40 flex items-start gap-1.5">
            <KeyRound className="w-3 h-3 mt-0.5 shrink-0" />
            A unique passcode will be minted automatically. Share it with your agents — they enter it at signup or in Settings to gain SMS + calling rights under your brand.
          </div>
        </div>
        <div className="p-4 border-t border-civix-border flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button onClick={create} disabled={busy} className="btn-primary text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Branded Calling ────────────────────────────────────────────────────────

function BrandedCalling({ profile, onRefresh }) {
  const [name, setName] = useState(profile?.brandedCalling?.displayName || '');
  const [busy, setBusy] = useState(false);
  const status = profile?.brandedCalling?.status || 'unsubmitted';

  async function submit() {
    if (!name || name.length > 15) { toast.error('Display name required (max 15 chars)'); return; }
    setBusy(true);
    try {
      await apiPost('/a2p/branded-calling', { displayName: name });
      toast.success('Submitted for review');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  }

  return (
    <div className="panel p-5">
      <h2 className="text-lg font-semibold">Branded Calling</h2>
      <p className="text-sm text-white/60 mt-1">
        Branded calling allows you to change what’s displayed on the caller ID when you call your leads. Enter a caller display name (max 15 alphanumeric characters including spaces) and submit it for review.
      </p>
      <p className="text-xs text-white/40 mt-2">
        Review takes up to 72 hours and is conducted by our telecom provider. After approval you can attach phone numbers from the Phone Numbers tab.
      </p>
      <div className="mt-4 flex gap-2 items-end">
        <div className="flex-1">
          <label className="label">Caller display name *</label>
          <input className="input mt-1" maxLength={15} value={name} onChange={(e) => setName(e.target.value)} placeholder="CIVIX LLC" />
        </div>
        <button onClick={submit} disabled={busy || !name} className="btn-primary text-sm h-10">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
        </button>
        <div className="h-10 flex items-center"><StatusPill status={status} /></div>
      </div>
    </div>
  );
}

// ─── Caller ID Reputation ──────────────────────────────────────────────────

function CallerIdReputation({ profile, onRefresh }) {
  const cir = profile?.callerIdReputation || {};
  const [busy, setBusy] = useState(false);

  async function remove() {
    if (!window.confirm('Delete caller ID reputation profile?')) return;
    setBusy(true);
    try { await apiDel('/a2p/caller-id-reputation'); toast.success('Deleted'); onRefresh(); }
    finally { setBusy(false); }
  }

  return (
    <div className="panel p-5">
      <h2 className="text-lg font-semibold">Caller ID Reputation Profile</h2>
      <p className="text-sm text-white/60 mt-1">
        Caller ID reputation is now part of the TrustHub Brand submission flow above. Once your Brand is submitted and Branded Calling is approved, your phone numbers are automatically registered with the carrier reputation services.
      </p>
      {cir.status === 'approved' ? (
        <div className="mt-3 panel-light p-3 border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-emerald-400" />
            Your caller ID reputation profile has been approved. All current and future phone numbers for everyone attached to this business profile will be automatically registered.
          </div>
          <button onClick={remove} disabled={busy} className="btn-ghost text-xs text-rose-300">Delete profile</button>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-3">
          <StatusPill status={cir.status || 'unsubmitted'} />
          <span className="text-xs text-white/40">Managed via Brand submission — no separate action required.</span>
        </div>
      )}
    </div>
  );
}

// ─── Agent Join Card ───────────────────────────────────────────────────────

function JoinCampaignCard({ onJoined }) {
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  async function lookup() {
    if (!code) return;
    setPreviewing(true); setPreview(null);
    try {
      const res = await apiGet(`/a2p/lookup/${code.trim()}`);
      setPreview(res.campaign);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Passcode not found');
    } finally { setPreviewing(false); }
  }

  async function join() {
    setBusy(true);
    try {
      await apiPost('/a2p/join', { passcode: code.trim() });
      toast.success('Joined! You can now send SMS and place calls.');
      onJoined?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  }

  return (
    <div className="panel p-6 max-w-2xl">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="w-5 h-5 text-civix-cyan" />
        <h2 className="text-lg font-semibold">Join your team’s A2P campaign</h2>
      </div>
      <p className="text-sm text-white/60">
        Your manager registered the business with the carriers and minted a campaign passcode. Enter it here to gain access to text and call clients compliantly.
      </p>

      <div className="mt-5 space-y-2">
        <label className="label">Campaign passcode</label>
        <div className="flex gap-2">
          <input
            className="input flex-1 font-mono"
            placeholder="a2p_..."
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button onClick={lookup} disabled={!code || previewing} className="btn-ghost text-sm">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-4 panel-light p-4 border-civix-cyan/40 bg-civix-cyan/5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40">Joining</div>
              <div className="font-semibold">{preview.businessName}</div>
              <div className="text-xs text-white/60 mt-1">{preview.useCaseDescription} · ${preview.monthlyCost}/mo passthrough</div>
            </div>
            <button onClick={join} disabled={busy} className="btn-primary text-sm flex items-center gap-1">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4" /> Join campaign</>}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 text-[11px] text-white/40 flex items-start gap-1.5">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Until you join, outbound SMS is disabled. (Receiving + replying is unaffected.) Ask your manager for the passcode — they’ll find it on the Business Profile page.
      </div>
    </div>
  );
}
